// @flow
import React from 'react';
import { Trans } from '@lingui/react';
import { captureMessage, withScope } from '@sentry/browser';
import HostedFields from 'braintree-web/hosted-fields';
import get from 'lodash-es/get';
import styled, { type StyledComponent } from 'styled-components';

import { DSTokenMap, FONT_FAMILY, FontSize, FontWeight, Spacing } from '@rover/kibble/styles';
import AddressForm from '@rover/react-lib/components/forms/AddressForm';
import SuccessOrErrorAlert from '@rover/react-lib/components/notifications/SuccessOrErrorAlert';
import { CARD_TYPES } from '@rover/react-lib/constants/creditCard.constants';
import type { Address } from '@rover/react-lib/types';
import type { CreditCardType } from '@rover/react-lib/types/CreditCard';
import { isValidAddress } from '@rover/react-lib/utils/address';
import { BrainTreeCreditCardConstants } from '@rover/shared/js/constants/translation';
import { getInstance } from '@rover/utilities/braintree';
import reportError from '@rover/utilities/reportError';

import Button, { ButtonType } from '../../../buttons/Button';
import LoadingButton from '../../../buttons/LoadingButton';
import Form from '../../../utils/Form';

import AddressCheckbox from './AddressCheckbox';
import CreditCardForm from './CreditCardForm';
import LoadingOverlay from './LoadingOverlay';

const getValidators = () => ({ creditCard: { hostedFieldsInstance }, address }) => {
  const hostedFieldsInstanceState = hostedFieldsInstance && hostedFieldsInstance.getState();
  return {
    creditCard: {
      cardholderName: [Form.validators.required, Form.validators.cardHolderName],
      creditCardNumber: () => {
        if (
          get(hostedFieldsInstanceState, 'fields.number.isEmpty') ||
          !get(hostedFieldsInstanceState, 'fields.number.isValid')
        ) {
          return BrainTreeCreditCardConstants.isInvalidCreditCardString;
        }
        return null;
      },
      expiration: () => {
        if (
          get(hostedFieldsInstanceState, 'fields.expirationDate.isEmpty') ||
          !get(hostedFieldsInstanceState, 'fields.expirationDate.isValid')
        ) {
          return BrainTreeCreditCardConstants.isInvalidExpirationDateString;
        }
        return null;
      },
      cvv: () => {
        if (
          get(hostedFieldsInstanceState, 'fields.cvv.isEmpty') ||
          !get(hostedFieldsInstanceState, 'fields.cvv.isValid')
        ) {
          return BrainTreeCreditCardConstants.isInvalidCVVString;
        }
        return null;
      },
    },
    ...AddressForm.getValidators(address),
  };
};

const CreditCardContainer: StyledComponent<{}> = styled.div`
  position: relative;
`;

const BillingAddressTitle: StyledComponent<{}> = styled.div`
  margin: ${Spacing.M} 0 ${Spacing.S} 0;
  font-weight: ${FontWeight.BOLD};
`;

const ButtonGroup: StyledComponent<{}> = styled.div`
  display: flex;
  justify-content: flex-end;
`;

// TODO: move this to a type file when we clean up
type CreditCardData = {
  creditCard: { cardholderName: string, hostedFieldsInstance: any },
  address: Address,
};

type Props = {
  address: Address,
  loading: boolean,
  onToggleForm: () => void,
  onSubmit: ({ creditCard: { cardholderName: string, nonce: string }, address: Address }) => void,
  nestedForm: boolean,
  hideCancelButton: boolean,
  supportedCountryCodes?: Array<string>,
  centerButtons?: boolean,
  saveButtonText?: string,
  errorMessage: string,
};

type State = {
  hostedFieldsInstance?: any,
  currentFocus: string,
  cardType: ?CreditCardType,
  usePrimaryAddress: boolean,
  braintreeError: string,
};

export default class BraintreeCreditCardForm extends React.Component<Props, State> {
  state = {
    hostedFieldsInstance: null,
    currentFocus: '',
    cardType: null,
    usePrimaryAddress: isValidAddress(this.props.address),
    braintreeError: '',
  };

  async componentDidMount() {
    this.logMessageToSentry('Initializing hosted fields');
    const braintreeInstance = await getInstance();
    await HostedFields.create(
      {
        client: braintreeInstance,
        styles: {
          input: {
            'font-size': `${FontSize.M}`,
            color: `${DSTokenMap.TEXT_COLOR_SECONDARY}`,
            'font-family': FONT_FAMILY,
          },
          'input::placeholder': {
            color: `${DSTokenMap.TEXT_COLOR_TERTIARY}`,
            opacity: 1,
          },
        },
        fields: {
          number: {
            selector: '#card-number',
            placeholder: BrainTreeCreditCardConstants.creditCardNumberPlaceholder,
          },
          cvv: {
            selector: '#cvv',
            placeholder: 'CVV',
          },
          expirationDate: {
            selector: '#expiration',
            placeholder: 'MM/YYYY',
          },
        },
      },
      this.hostedFieldsDidCreate
    );
  }

  componentWillUnmount() {
    this.logMessageToSentry('Tearing down hosted fields');
    if (this.state.hostedFieldsInstance) {
      this.state.hostedFieldsInstance.teardown((teardownErr) => {
        if (teardownErr) {
          reportError(new Error(`Could not tear down Hosted Fields! ${teardownErr}`));
        }
      });
    }
  }

  hostedFieldsDidCreate = (err: Object, hostedFieldsInstance: any) => {
    if (err) this.logMessageToSentry('Error initializing Hosted fields', err);

    this.setState({
      hostedFieldsInstance,
    });
    hostedFieldsInstance.on('focus', (stateObj) => {
      this.setState({
        currentFocus: stateObj.emittedBy,
      });
    });
    hostedFieldsInstance.on('blur', () => {
      this.setState({
        currentFocus: '',
      });
    });
    hostedFieldsInstance.on('cardTypeChange', ({ cards, fields }) => {
      if (fields.number.isEmpty) {
        this.setState({
          cardType: null,
        });
      } else {
        const reconizedTypes = cards.map(({ type }) => CARD_TYPES[type]).filter(Boolean);
        if (reconizedTypes.length) {
          this.setState({
            cardType: reconizedTypes[0].display,
          });
        }
      }
    });
    this.logMessageToSentry('Hosted fields initialized');
  };

  // TODO: Remove once we have enough info to solve DEV-52152
  logMessageToSentry = (message: string, extras: {} = {}) => {
    withScope((scope) => {
      const { currentFocus, cardType, usePrimaryAddress, braintreeError } = this.state;
      scope.setExtras({ currentFocus, cardType, usePrimaryAddress, braintreeError, ...extras });
      captureMessage(`Braintree Debugging: ${message}`);
    });
  };

  getHandleSubmit = (
    onSubmit: ({ creditCard: { cardholderName: string, nonce: string }, address: Address }) => void,
    onToggleForm: () => void
  ) => async ({ creditCard, address }: CreditCardData) => {
    this.logMessageToSentry('Beginning credit card submission', { postalCode: address.zip });
    this.setState(() => ({
      braintreeError: '',
    }));
    try {
      const { nonce } = await creditCard.hostedFieldsInstance.tokenize({
        billingAddress: {
          postalCode: address.zip,
        },
      });
      this.logMessageToSentry('Successfully retrieved nonce', { nonce });
      const isSuccessful = await onSubmit({
        creditCard: {
          cardholderName: creditCard.cardholderName,
          nonce,
        },
        address,
        formVersion: 'braintree',
      });
      this.logMessageToSentry(`Submission was ${isSuccessful ? '' : 'not'} successful`, {
        errorMessage: this.props.errorMessage,
      });
      if (isSuccessful && onToggleForm) {
        onToggleForm();
      }
    } catch (e) {
      this.setState(() => ({
        braintreeError: BrainTreeCreditCardConstants.braintreeErrorMessage,
      }));
      this.logMessageToSentry('Error thrown while submitting');
      reportError(e);
    }
  };

  render() {
    const {
      address,
      loading,
      onSubmit,
      nestedForm,
      onToggleForm,
      hideCancelButton,
      supportedCountryCodes,
      centerButtons,
      saveButtonText,
    } = this.props;
    const {
      hostedFieldsInstance,
      cardType,
      currentFocus,
      usePrimaryAddress,
      braintreeError,
    } = this.state;
    const buttonGroupStyle = centerButtons ? { justifyContent: 'center' } : {};
    return (
      <Form
        onSubmit={this.getHandleSubmit(onSubmit, onToggleForm)}
        data-qa-id="credit-card-forms-add-card"
        nested={nestedForm}
        initialData={
          ({
            creditCard: {
              hostedFieldsInstance,
              cardholderName: '',
            },
            address,
          }: CreditCardData)
        }
        // validators will return more fields than the data since hostedFieldInstance is holding onto some of the data
        validators={getValidators()}
      >
        {(form) => (
          <div>
            <SuccessOrErrorAlert error={braintreeError} />
            <CreditCardContainer>
              <CreditCardForm form={form} cardType={cardType} currentFocus={currentFocus} />
              {!hostedFieldsInstance && <LoadingOverlay />}
            </CreditCardContainer>
            <BillingAddressTitle>
              <Trans>Billing Address</Trans>
            </BillingAddressTitle>
            {isValidAddress(address) && (
              <AddressCheckbox
                onChange={() => {
                  this.setState({
                    usePrimaryAddress: !usePrimaryAddress,
                  });
                }}
                address={address}
                isChecked={usePrimaryAddress}
              />
            )}
            {!usePrimaryAddress && (
              <AddressForm
                address={form.data.address}
                onChange={(changes) => {
                  form.onChange({ address: changes });
                }}
                validationErrors={form.validationErrors}
                supportedCountryCodes={supportedCountryCodes}
              />
            )}
            <ButtonGroup style={buttonGroupStyle}>
              {!hideCancelButton && (
                <Button buttonType={ButtonType.TEXT} onClick={onToggleForm}>
                  <Trans>Cancel</Trans>
                </Button>
              )}
              <LoadingButton
                loading={loading}
                buttonType={ButtonType.PRIMARY}
                onClick={form.onSubmit}
                data-qa-id="credit-card-buttons-save"
              >
                {saveButtonText || <Trans>Save Card</Trans>}
              </LoadingButton>
            </ButtonGroup>
          </div>
        )}
      </Form>
    );
  }
}



// WEBPACK FOOTER //
// ../react-lib/src/components/forms/CreditCardForm/BraintreeCreditCardForm/BraintreeCreditCardForm.jsx